import { defineStore } from 'pinia';
import formatHelper from '@nextories/vue-core-components/src/utils/helpers/format';
import offersHelper from '@/utils/helpers/offers';
import { useProjectStore } from './projectStore';
import { useServiceStore } from './serviceStore';
import type { Offer } from '@/types/Offers';
import { useDemandStore } from './demandStore';
import type { ConfigState, ConfigSection, ConfigType, PaymentType, BaseStepConfig } from '@/types/Config';
import { usePaymentsStore } from './paymentsStore';

const installmentConfig = {
  subStep: 'payment',
  title: 'installment.title',
  loadingText: 'installment.loadingText',
  subtitle: 'installment.subtitle',
  content: 'installment.content',
  previous: null,
  next: null,
};

export const paymentTypes = {
  creditCard: {
    key: 'credit_card',
    display: true,
    options: {
      title: 'payment.preBooking.paymentTypes.creditCard.options.title',
      content: 'payment.preBooking.paymentTypes.creditCard.options.content',
    },
  },
  installment: {
    key: 'installment',
    nbStep: 2,
    subStep: 'payment',
    display: true,
    options: {
      title: 'payment.preBooking.paymentTypes.installment.options.title',
      content: 'payment.preBooking.paymentTypes.installment.options.content',
      plan: {
        title: 'installment.preBooking.plan.title',
        content: 'installment.preBooking.plan.content',
        subtitle: 'installment.preBooking.plan.subtitle',
        totalAmount: 'installment.preBooking.plan.totalAmount',
        after: {
          totalAmount: 'installment.preBooking.plan.after.totalAmount',
        },
        fees: 'installment.preBooking.plan.fees',
        scheduleRank: {
          2: 'installment.preBooking.plan.scheduleRank.2',
          3: 'installment.preBooking.plan.scheduleRank.3',
          4: 'installment.preBooking.plan.scheduleRank.4',
        },
        rank: {
          1: 'installment.preBooking.plan.rank.1',
          2: 'installment.preBooking.plan.rank.2',
          3: 'installment.preBooking.plan.rank.3',
          4: 'installment.preBooking.plan.rank.4',
        },
      },
    },
    loading: {
      loadingText: 'payment.preBooking.paymentTypes.installment.loading.loadingText',
      subtitle: 'payment.preBooking.paymentTypes.installment.loading.subtitle',
    },
  },
  bankTransfer: {
    key: 'bank_transfer',
    display: true,
    options: {
      title: 'payment.preBooking.paymentTypes.bankTransfer.options.title',
      content: 'payment.preBooking.paymentTypes.bankTransfer.options.content',
    },
  },
};

export const defaultConfig = {
  procedures: {
    title: 'comparator.cardFlex.message',
    button: {
      name: 'procedures',
      text: 'comparator.cardFlex.button',
    },
  },
  services: {
    nbStep: 1,
    subtitle: 'services.title',
    previous: {
      name: 'comparator',
      text: 'services.services.alter',
    },
    next: {
      name: 'resume',
      text: 'services.services.submit',
    },
  },
  resume: {
    title: 'resume.title',
    loadingText: 'resume.loadingText',
    showTopBackButton: true,
    card: {
      content: 'resume.booking.cardContent',
      icon: ['fas', 'lightbulb-on'],
    },
    subtitle: null,
    previous: {
      name: 'comparator',
      text: 'resume.button.back',
    },
    infoStep: {
      icon: ['fas', 'lightbulb-on'],
    },
    // TODO use this version of previous when we activate services
    // previous: {
    //   name: 'services',
    //   text: 'resume.button.update',
    // },
    next: {
      name: 'confirm',
      text: 'base.next',
    },
    options: {
      allowDateChange: true,
    },
  },
  payment: {
    title: 'payment.title',
    loadingText: 'payment.loadingText',
    subtitle: 'payment.subtitle',
    card: {
      content: 'payment.card.content',
    },
    options: {
      displayNotice: false,
      displaySecurity: true,
      paymentTypes: {
        creditCard: {
          ...paymentTypes.creditCard,
          options: {
            ...paymentTypes.creditCard.options,
            content: null,
          },
        },
        bankTransfer: {
          ...paymentTypes.bankTransfer,
          content: 'payment.booking.contentBankTransfer',
          footerCard: {
            content: 'payment.booking.footerCardContentBankTransfer',
          },
          next: {
            text: 'payment.booking.bookBankTransfer',
          },
          options: {
            ...paymentTypes.bankTransfer.options,
            content: null,
          },
        },
      },
    },
    infoStep: {
      icon: ['fas', 'lightbulb-on'],
    },
    previous: {
      name: 'resume',
    },
    next: {
      text: 'payment.booking.book',
    },
  },
  signature: {
    title: 'signature.book.title',
    subtitle: 'signature.book.subtitle',
    content: 'signature.book.content',
    card: {
      icon: ['fal', 'signature'],
      content: 'signature.card.content',
    },
    next: {
      text: 'signature.button.sign',
      action: 'nextStep',
    },
    options: {
      autoSigningModal: true,
    },
  },
  confirm: {
    infoStep: {
      icon: ['fas', 'lightbulb-on'],
    },
    title: 'confirm.book.title',
    subtitle: 'confirm.book.subtitle',
    content: 'confirm.book.content',
    options: {
      descriptionMover: 'confirm.description.default',
      bottomDescription: 'confirm.description.bottom',
    },
  },
};

export const commissionnaireConfig = {
  prebook: {
    services: null,
    resume: {
      nbStep: 1,
      card: {
        content: 'resume.preBooking.commitmentInformation',
      },
      footerText: 'resume.preBooking.footerTextCommissionnaire',
      next: {
        action: 'prebookActionWithWarning',
        text: 'resume.button.prebook',
      },
      options: {
        beforePrice: {
          title: 'resume.price.title',
        },
      },
    },
    payment: null,
    signature: null,
    confirm: {
      nbStep: 2,
      title: 'confirm.prebook.title',
      subtitle: 'confirm.prebook.subtitle',
      content: 'confirm.prebook.content',
      next: {
        name: 'procedures',
        text: 'confirm.toSeeMoving',
      },
    },
  },
  book: {
    services: null,
    resume: {
      nbStep: 1,
      title: null,
      subtitle: 'resume.booking.title',
      showTopBackButton: false,
      next: {
        action: 'bookAction',
        text: 'resume.button.valid',
        name: 'signature',
      },
      options: {
        optionsType: 'chosen',
        allowDateChange: false,
      },
    },
    payment: null,
    signature: {
      nbStep: 2,
    },
    confirm: {
      nbStep: 3,
      next: {
        name: 'procedures',
        text: 'confirm.toSeeMoving',
      },
    },
  },
};

export const minarmConfig = {
  prebook: {
    services: null,
    resume: {
      title: 'resume.preBooking.title',
      nbStep: 1,
      card: {
        content: 'resume.preBooking.commitmentInformationMinarm',
        icon: null,
      },
      footerText: 'resume.preBooking.footerText',
      next: {
        action: 'prebookActionMinarm',
        name: 'confirm',
        text: 'resume.button.prebook',
      },
    },
    payment: null,
    signature: null,
    confirm: {
      nbStep: 2,
      title: 'confirm.prebook.title',
      subtitle: 'confirm.prebook.subtitle',
      next: {
        name: 'procedures',
        text: 'confirm.button.procedures',
      },
      content: 'confirm.prebook.contentMinarm',
    },
  },
  book: {
    services: null,
    resume: {
      nbStep: 1,
      title: null,
      subtitle: 'resume.booking.titleMinarm',
      footerText: 'resume.booking.footerText',
      next: {
        action: 'bookAction',
        text: 'resume.button.valid',
        name: 'signature',
      },
      card: null,
      options: {
        optionsType: 'chosen',
        allowDateChange: false,
      },
    },
    payment: null,
    signature: {
      nbStep: 2,
    },
    confirm: {
      nbStep: 3,
      next: {
        name: 'procedures',
        text: 'confirm.button.procedures',
      },
    },
  },
};

export const payingMinarmConfig = {
  ...minarmConfig,
  book: {
    ...minarmConfig.book,
    resume: {
      ...minarmConfig.book.resume,
      next: {
        name: 'payment',
      },
    },
    payment: {
      nbStep: 2,
      title: null,
      subtitle: 'payment.titlePCD',
      card: {
        icon: ['far', 'credit-card'],
        content: 'payment.card.book.contentMinarm',
      },
      options: {
        paymentTypes: {
          creditCard: paymentTypes.creditCard,
          bankTransfer: null,
        },
      },
      loadingText: 'payment.loadingTextB2BWithPlafond',
      next: {
        name: 'signature',
        action: 'bookAction',
        text: 'payment.booking.bookMinarm',
      },
    },
    signature: {
      ...minarmConfig.book.signature,
      nbStep: 3,
    },
    confirm: {
      ...minarmConfig.book.confirm,
      nbStep: 4,
    },
  },
};

export const individualConfig = {
  prebook: {
    services: null,
    resume: {
      nbStep: 1,
      title: 'resume.preBooking.title',
      infoStep: {
        content: 'resume.preBooking.infoStep.content',
      },
      card: null,
      next: {
        name: 'payment',
        action: 'saveOptionAndPrebookDate',
      },
      options: {
        boxes: [{
          color: 'success',
          icon: 'circle-euro',
          content: 'resume.boxes.paiement',
        }, {
          color: 'success',
          icon: 'handshake',
          content: 'resume.boxes.cancel',
        }, {
          color: 'success',
          icon: 'lock-keyhole',
          content: 'resume.boxes.insurance',
        }],
      },
    },
    payment: {
      nbStep: 2,
      title: 'payment.preBooking.title',
      subtitle: 'payment.preBooking.subtitle',
      loadingTitle: 'payment.preBooking.loadingTitle',
      loadingText: 'payment.preBooking.loadingText',
      infoStep: {
        content: 'payment.preBooking.infoStep.content',
      },
      card: null,
      content: 'payment.preBooking.content',
      previous: {
        name: 'resume',
      },
      options: {
        displaySecurity: false,
        displayNotice: false,
        paymentTypesTitle: 'payment.paymentTypes.title',
        paymentTypes: {
          ...paymentTypes,
          bankTransfer: {
            ...paymentTypes.bankTransfer,
            content: null,
            footerCard: null,
            next: null,
          },
        },
      },
      next: {
        name: 'confirm',
        action: 'prebookAction',
        text: 'payment.actions.prebook',
      },
    },
    installment: installmentConfig,
    confirm: {
      nbStep: 3,
      infoStep: {
        content: 'confirm.prebook.infoStep.content',
      },
      title: 'confirm.prebook.title',
      subtitle: 'confirm.prebook.subtitle',
      content: 'confirm.prebook.content',
      next: {
        name: 'details',
        text: 'confirm.toSeeMoving',
      },
      options: {
        ...defaultConfig.confirm.options,
        paymentTypes: {
          bankTransfer: {
            content: 'confirm.prebook.contentBankTransfer',
          },
        },
      },
    },
  },
  book: {
    services: null,
    resume: {
      nbStep: 1,
      title: 'resume.booking.title',
      infoStep: {
        content: 'resume.booking.infoStep.content',
      },
      card: {
        icon: null,
      },
      next: {
        name: 'payment',
        text: 'resume.booking.book',
      },
      options: {
        allowDateChange: false,
        optionsType: 'chosen',
      },
    },
    payment: {
      nbStep: 2,
      content: 'payment.booking.content',
      loadingSubtitle: 'payment.booking.loadingSubtitle',
      footerCard: {
        icon: ['fas', 'lightbulb-on'],
        image: null,
        content: 'payment.booking.footerCardContent',
      },
      options: {
        displaySecurity: false,
        totalPrice: {
          title: 'payment.booking.totalPrice.title',
          arrhes: 'payment.booking.totalPrice.arrhesAmount',
          solde: 'payment.booking.totalPrice.solde',
        },
      },
      next: {
        name: 'signature',
        action: 'bookAction',
      },
    },
    signature: {
      nbStep: 3,
      title: 'signature.book.titleIndividual',
      subtitle: 'signature.book.subtitleIndividual',
      infoStep: {
        content: 'signature.book.infoStep.content',
      },
      content: 'signature.book.content',
      card: null,
      memoCard: {
        icon: ['fas', 'lightbulb-on'],
        content: 'signature.book.memo',
      },
      options: {
        autoSigningModal: true,
      },
    },
    confirm: {
      nbStep: 4,
      title: 'confirm.book.titleIndividual',
      next: {
        name: 'procedures',
        text: 'confirm.toSeeMoving',
      },
    },
  },
};

export const individualInstallmentConfig = {
  prebook: {
    ...individualConfig.prebook,
    confirm: {
      ...individualConfig.prebook.confirm,
      options: undefined,
    },
  },
  book: {
    ...individualConfig.book,
    payment: {
      ...individualConfig.book.payment,
      component: 'installment-confirm',
      title: 'installment.booking.title',
      content: 'installment.booking.content',
      subtitle: 'installment.booking.subtitle',
      options: {
        ...paymentTypes.installment,
        displayNotice: false,
        displayAddCard: false,
        confirm: {
          subtitle: 'installment.booking.confirm.subtitle',
          notice: 'installment.booking.confirm.notice',
        },
        plan: {
          subtitle: 'installment.booking.plan.subtitle',
          after: {
            totalAmount: 'installment.booking.plan.after.totalAmount',
          },
          scheduleRank: {
            2: 'installment.booking.plan.scheduleRank.2',
            3: 'installment.booking.plan.scheduleRank.3',
            4: 'installment.booking.plan.scheduleRank.4',
          },
          rank: {
            1: 'installment.booking.plan.rank.1',
            2: 'installment.booking.plan.rank.2',
            3: 'installment.booking.plan.rank.3',
            4: 'installment.booking.plan.rank.4',
          },
        },
        after: {
          totalAmount: 'installment.booking.after.totalAmount',
        },
      },
      next: {
        ...individualConfig.book.payment.next,
        text: 'installment.booking.book',
      },
    },
  },
};

export const individualFloaAlreadyPaidConfig = {
  prebook: {
    ...individualConfig.prebook,
    payment: {
      ...individualConfig.prebook.payment,
      options: {
        ...individualConfig.prebook.payment.options,
        paymentTypes: {
          installment: {
            ...individualConfig.prebook.payment.options.paymentTypes.installment,
            display: true,
          },
          bankTransfer: null,
          creditCard: null,
        },
      },
      next: {
        ...individualConfig.prebook.payment.next,
        action: 'prebookAction',
        text: 'payment.actions.prebook',
      },
    },
    confirm: {
      ...individualConfig.prebook.confirm,
      options: undefined,
    },
  },
};

export const individualArrhesPaidConfig = {
  ...individualConfig,
  prebook: {
    ...individualConfig.prebook,
    payment: {
      ...individualConfig.prebook.payment,
      displayNotice: false,
      options: {
        ...individualConfig.prebook.payment.options,
        paymentTypes: {
          ...individualConfig.prebook.payment.options.paymentTypes,
          creditCard: {
            ...individualConfig.prebook.payment.options.paymentTypes.creditCard,
            displayNotice: true,
            content: null,
            footerCard: null,
            next: null,
          },
          installment: null,
        },
      },
    },
  },
  book: {
    ...individualConfig.book,
    payment: {
      ...individualConfig.book.payment,
      content: 'payment.booking.contentIndividualArrhesPaid',
      options: {
        ...individualConfig.book.payment.options,
        totalPrice: {
          ...individualConfig.book.payment.options.totalPrice,
          arrhes: 'payment.booking.totalPrice.arrhesAmountIndividualPaid',
        },
      },
      next: {
        ...individualConfig.book.payment.next,
        text: 'payment.booking.bookIndividualArrhesPaid',
      },
    },
  },
};

export const individualArrhesAlreadyPaidConfig = {
  ...individualConfig,
  prebook: {
    ...individualConfig.prebook,
    payment: {
      ...individualConfig.prebook.payment,
      options: {
        ...individualConfig.prebook.payment.options,
        displayNotice: false,
        paymentTypes: {
          ...individualConfig.prebook.payment.options.paymentTypes,
          creditCard: {
            ...individualConfig.prebook.payment.options.paymentTypes.creditCard,
            displayNotice: true,
            content: null,
            footerCard: null,
            next: null,
          },
          installment: null,
        },
      },
    },
  },
  book: {
    ...individualConfig.book,
    payment: {
      ...individualConfig.book.payment,
      content: 'payment.booking.contentIndividualWithArrhesAlreadyPaid',
      options: {
        ...individualConfig.book.payment?.options,
        showOnlyDefault: true,
        displayAddCard: false,
        displayNotice: false,
        totalPrice: {
          ...individualConfig.book.payment?.options?.totalPrice,
          arrhes: 'payment.booking.totalPrice.arrhesAmountIndividualWithArrhesAlreadyPaid',
        },
        paymentTypes: {
          ...defaultConfig.payment.options.paymentTypes,
          bankTransfer: {
            ...defaultConfig.payment.options.paymentTypes.bankTransfer,
            subtitle: 'payment.booking.subtitleBankTransfer',
          },
        },
      },
      next: {
        ...individualConfig.book.payment?.next,
        text: 'payment.booking.bookIndividualWithArrhesAlreadyPaid',
      },
    },
  },
};

export const individualDisabledPaymentConfig = {
  ...individualConfig,
  prebook: {
    ...individualConfig.prebook,
    resume: {
      ...individualConfig.prebook.resume,
      next: {
        name: 'confirm',
        action: 'preBookAction',
        text: 'payment.actions.prebook',
      },
    },
    confirm: {
      ...individualConfig.prebook.confirm,
      options: undefined,
      nbStep: 2,
    },
    payment: null,
  },
  book: {
    ...individualConfig.book,
    resume: {
      ...individualConfig.book.resume,
      next: {
        name: 'signature',
        action: 'bookAction',
      },
      options: {
        ...individualConfig.book.resume.options,
        totalPrice: {
          content: null,
          arrhes: 'resume.booking.totalPrice.arrhesAmountDisabledPayment',
        },
      },
    },
    payment: null,
    signature: {
      ...individualConfig.book.signature,
      nbStep: 2,
    },
    confirm: {
      ...individualConfig.book.confirm,
      nbStep: 3,
    },
  },
};

export const payingCommissionnaireConfig = {
  ...commissionnaireConfig,
  book: {
    services: null,
    resume: {
      ...commissionnaireConfig.book.resume,
      next: {
        name: 'payment',
        text: 'resume.button.valid',
      },
    },
    payment: {
      subtitle: 'payment.titlePCD',
      nbStep: 2,
      card: {
        content: 'payment.card.book.content',
      },
      options: {
        paymentTypes: {
          creditCard: paymentTypes.creditCard,
          bankTransfer: null,
        },
      },
      next: {
        name: 'signature',
        action: 'bookAction',
      },
    },
    signature: {
      nbStep: 3,
    },
    confirm: {
      nbStep: 4,
      next: {
        name: 'procedures',
        text: 'confirm.toSeeMoving',
      },
    },
  },
};

export const courtageConfig = {
  book: {
    procedures: null,
    services: null,
    resume: {
      nbStep: 1,
      subtitle: null,
      card: {
        content: 'resume.booking.validateLabel',
      },
      previous: {
        name: 'comparator',
      },
      next: {
        name: 'confirm',
        action: 'chooseAction',
        text: 'resume.choose.choose',
      },
      options: {
        allowDateChange: false,
      },
      loadingText: 'confirm.book.loadingText',
    },
    payment: null,
    signature: null,
    confirm: {
      nbStep: 2,
      loadingText: 'confirm.book.loadingText',
      next: {
        name: 'home',
        text: 'confirm.toSeeMoving',
      },
      content: 'confirm.book.contentCourtage',
    },
  },
};

const CONFIG_BY_KEY: Record<string, ConfigType> = {
  courtageConfig,
  commissionnaireConfig,
  minarmConfig,
  payingMinarmConfig,
  individualConfig,
  individualInstallmentConfig,
  individualFloaAlreadyPaidConfig,
  individualArrhesPaidConfig,
  individualArrhesAlreadyPaidConfig,
  individualDisabledPaymentConfig,
  payingCommissionnaireConfig,
};

export const useConfigStore = defineStore('config', {
  state: (): ConfigState => ({
    config: defaultConfig,
    isConfigLoading: false,
    configError: null,
    comparatorConfig: {},
    contentWarningModal: null,
    warningModal: null,
    isWarningModalDisplayed: false,
    loading: false,
    stepLoading: false,
    buttonsDisabled: false,
    nextButtonDisabled: false,
    totalComparatorStep: 4,
    offer: null,
    isPrebooking: false,
    keyConfig: '',
  }),

  actions: {
    setConfigLoading(loading: boolean) {
      this.isConfigLoading = loading;
    },

    displayWarningModal(content: { display: boolean; message: string } | null) {
      this.warningModal = content;
      this.isWarningModalDisplayed = !!content?.display;
    },

    setConfigError(error: any) {
      this.configError = error;
    },

    resetConfig() {
      this.config = defaultConfig;
      this.configError = null;
    },

    setConfig(offer: Offer) {
      const paymentsStore = usePaymentsStore();
      paymentsStore.$reset();
      const projectStore = useProjectStore();
      const demandStore = useDemandStore();
      const projectSelected = projectStore.projectSelected;
      if (!projectSelected) {
        return;
      }
      const params = projectSelected.params || {};
      const isCBPaymentDisabled = params.is_cb_payment_disabled;
      const isCommissionaire = params.is_commissionaire;
      const isMinarm = params.is_minarm;
      const isIndividual = params.is_individual;
      const isValidationDoneByPartner = params.partner_validates_quotes;

      let key: keyof typeof CONFIG_BY_KEY;
      if (!isCommissionaire) {
        key = 'courtageConfig';
      } else if (isIndividual) {
        key = 'individualConfig';
        if (offer?.floa_payment?.dealReference) {
          key = 'individualInstallmentConfig';
        } else if (!!demandStore.demand?.floa_already_paid?.dealReference) {
          key = 'individualFloaAlreadyPaidConfig';
        } else if (isCBPaymentDisabled) {
          key = 'individualDisabledPaymentConfig';
        } else if (demandStore.demand?.arrhes_already_paid) {
          key = 'individualArrhesAlreadyPaidConfig';
        } else if (offer?.arrhes_payment?.status === 'succeeded') {
          key = 'individualArrhesPaidConfig';
        }
      } else if (offer && params.ceiling) {
        const serviceStore = useServiceStore();
        const ceiling = offersHelper.getRepaymentCeiling(offer) || 0;
        const mustPay = !isCBPaymentDisabled
          && ceiling > 0
          && offersHelper.getPrice(offer, false)
          + serviceStore.servicesPrice > ceiling;
        if (isMinarm) {
          key = mustPay ? 'payingMinarmConfig' : 'minarmConfig';
        } else {
          key = mustPay ? 'payingCommissionnaireConfig' : 'commissionnaireConfig';
        }
      } else {
        key = 'commissionnaireConfig';
      }
      this.keyConfig = key;
      const config = CONFIG_BY_KEY[key];
      // Check if we are prebooking or booking
      this.isPrebooking = isCommissionaire
        && (!offer || !offersHelper.isValidOffer(offer, isIndividual || !isValidationDoneByPartner));
      let configState: ConfigSection = JSON.parse(JSON.stringify(this.isPrebooking ? config.prebook : config.book));

      this.totalComparatorStep = Object.keys(configState).filter((x) => {
        const existingConfig: BaseStepConfig | { subStep?: string | null } | null | undefined = configState[x as keyof ConfigSection];
        return existingConfig && existingConfig !== null && !existingConfig.subStep;
      }).length;

      this.offer = offer;

      // Override the default config for each key
      configState = formatHelper.deepMergeFlatten(defaultConfig, configState);

      if (this.isPrebooking && this.offer?.floa_payment?.dealReference) {
        configState.installment = installmentConfig;
      } else {
        configState.installment = null;
      }
      this.comparatorConfig = configState;
      paymentsStore.paymentTypesConfig = this.comparatorConfig?.payment?.options;
    },
    setLoading(value: boolean) {
      this.loading = value;
    },
    setStepLoading(value: boolean) {
      this.stepLoading = value;
    },
    setIsWarningModalDisplayed(content: { display: boolean; message: string } | null) {
      this.warningModal = content;
      this.isWarningModalDisplayed = !!content?.display;
    },
    setOffer(offer: Offer) {
      const projectStore = useProjectStore();
      this.offer = projectStore.projectSelected && offersHelper.formatOffer(offer, projectStore.projectSelected);
    },
  },
});
